import styled, { css } from 'styled-components';
import { Popper } from '@mui/material';

import colors from '../../styles/colors';
import Button from '../Button';
import { mixins } from '../../styles/breakpoints';

const Container = css`
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;
  gap: 20px;
`;

/* stylelint-disable no-duplicate-selectors */
const ContainerInbound = styled.div`
  ${Container}
  flex-direction: row;
  justify-content: left;
`;

const ContainerOutbound = styled.div`
  ${Container}
  flex-direction: row-reverse;
  justify-content: right;
`;

// Column wrapper for one message: holds the bubble + (optional) failed caption,
// so the caption sits directly under the bubble, outside its background.
const MessageColumn = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 50%;
  ${mixins.upToMediumTabletMedia(css`
    max-width: 70%;
  `)}
  margin-bottom: 10px;

  ${ContainerInbound} & {
    align-items: flex-start;
  }
  ${ContainerOutbound} & {
    align-items: flex-end;
  }
`;

// The tail points at the sender's avatar. Only the bubble that actually renders an avatar gets one
// — mid-run bubbles in a mirrored-sender group would otherwise point at empty space.
const bubbleTail = css`
  &::before {
    content: '';
    width: 16px;
    height: 16px;
    position: absolute;
    top: 13px;

    ${ContainerInbound} & {
      left: -13px;
      background-color: ${colors.shades.info2};
      margin-right: -4px;
      clip-path: polygon(
        0% 0%,
        100% 0%,
        100% 97%
      );
    }
    ${ContainerOutbound} & {
      right: -13px;
      background-color: ${colors.shades.primary1};
      margin-left: -4px;
      clip-path: polygon(
        0% 0%,
        100% 0%,
        0% 97%
      );
    }
  }
`;

// max-width keeps the bubble inside MessageColumn: a 600px file preview would otherwise hold it at
// 620px, and outbound bubbles overflow to the left, where the message list cannot scroll.
const TextMediaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  position: relative;
  height: auto;
  max-width: 100%;
  padding: 10px;
  border-radius: 5px;
  word-wrap: break-word;
  word-break: break-word;
  ${ContainerInbound} & {
    background-color: ${colors.shades.info2};
  }
  ${ContainerOutbound} & {
    background-color: ${colors.shades.primary1};
  }

  ${(props) => props.$showTail && bubbleTail}
`;

// SentAt and EditedText render INSIDE TextMediaContainer, so this grey sits on the bubble, not on the
// white pane — and the two bubbles give it very different readings:
//
//   gamma10 on inbound  info2    #C1D3F2   3.33:1   (was 2.40:1 on the old sage inbound bubble)
//   gamma10 on outbound primary1 #96BCA1   2.40:1   (was 3.97:1 on the old mint outbound bubble)
//
// Both fail AA for 12px text and both did before this change, so it is not a new class of problem —
// but outbound moved the wrong way, and outbound is the majority of bubbles. It is not fixable by
// swapping greys: every other grey in the palette is lighter than gamma10, and clearing 4.5:1 on the
// sage bubble needs something near ink, which stops the timestamp reading as secondary text at all.
// That is a design call about how timestamps are treated, tracked in S2-9055, not a token swap.
const DetailText = css`
  font-size: 0.75rem;
  line-height: 7px;
  color: ${colors.shades.gamma10};
  margin-top: 3px;
`;

const SentAt = styled.div`
  ${DetailText}
`;

const ThreeDotsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.base.secondary2};
  ${ContainerInbound} & {
    justify-content: end;
  }
  ${ContainerOutbound} & {
    justify-content: start;
    flex-direction: row-reverse;
  }
`;

const MessageDetailsContainer = styled.div`
  display: flex;
  ${ContainerInbound} & {
    align-self: flex-start;
  }
  ${ContainerOutbound} & {
    align-self: flex-end;
    flex-direction: row-reverse;
  }
`;

const EditedText = styled.div`
  ${DetailText}
  ${ContainerInbound} & {
    margin-left: 5px;
  }
  ${ContainerOutbound} & {
    margin-right: 5px;
  }
`;

// Sender name shown as the first line INSIDE the first bubble of a consecutive-sender run
// (WhatsApp/Telegram style; real Slack sender snapshotted onto @Avida-mirrored messages).
// Only ever rendered inbound — mirrored messages never come from the viewer.
//
// Colour: secondary3 on the blue inbound bubble (info2 #C1D3F2) gives ~11.8:1 contrast. Greys
// remain unreadable here — gamma10 is still only ~3.3:1 against the bubble — so the dark token stays.
// Note this label no longer separates from the body text by HUE: the Avida palette collapses the old
// navy/near-black pair into one ink family, so secondary3 (#0F1A16) and the body's ink (#16201D) sit
// within 1.1:1 of each other. Separation is now carried by the 600 weight and the label's position as
// the bubble's first line. A green (secondary1) was tried to restore a hue distinction and rejected:
// 3.7:1 on the bubble fails AA for 12px text, and legibility outranks decoration.
const SenderNameInBubble = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${colors.shades.secondary3};
  margin-bottom: 4px;
`;

// Fixed width matching ChatUserProfilePicture's size={30} so the column holds its space even
// when the avatar is hidden (non-last message of a mirrored-sender run) — keeps bubbles aligned.
const ProfilePictureContainer = styled.div`
  display: flex;
  flex: none;
  width: 30px;
  padding-bottom: 30px;
`;

const ThreeDotsButton = styled(Button)`
  width: 18px;
  height: 10px;
  padding: 0;
  margin-bottom: 10px;
  border-radius: 5px;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: ${colors.base.primary};
    opacity: 0.75;
  }
`;

const ReactionTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  padding: 0 2px;
  margin-bottom: 10px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: ${colors.base.secondary2};
  cursor: pointer;

  svg {
    font-size: 14px;
  }

  &:hover {
    background: ${colors.base.primary};
    opacity: 0.75;
  }
`;

const InternalMessage = styled.div`
  padding: 20px 15px 10px 15px;
  margin: 10px 0;
  width: fit-content;
  border-radius: 10px;
  background: ${colors.shades.secondary6};
  position: relative;
`;

const InternalMessageTitle = styled.div`
  white-space: nowrap;
  font-size: 0.75rem;
  padding: 0 10px;
  margin: -20px  0.5rem 0 -15px;
  border-radius: 10px 0 10px 0;
  background: ${colors.shades.gamma2};
  color: ${colors.shades.primary4};
`;

const TextWrapper = styled.span`
  & p {
    white-space: pre-line;
  }
  & pre {
    white-space: pre-line;
  }
`;

// The quoted message has no border or left rule, so the background is the only thing separating it
// from the bubble it sits in — and the two sides have to reach that separation in opposite directions.
// Inbound's bubble (info2 #C1D3F2) is light, so the quote goes darker: info3 at 1.48:1. Outbound's
// bubble (primary1 #96BCA1) is mid-tone, so every deeper green lands at 1.18-1.24:1 and the quote has
// to go lighter instead: mint at 1.65:1, with ink at 13.2:1 on it. Mint is also simply the colour the
// coach bubble itself just vacated.
//
// Do not "restore symmetry" by putting primary6 back on outbound: #8DAE68 on #96BCA1 is 1.19:1, which
// is the pairing info3 exists to get away from, and outbound is ~110 of every 120 bubbles rendered.
const ReplyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  height: auto;
  width: 100%;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  word-wrap: break-word;
  word-break: break-word;
  ${ContainerInbound} & {
    background-color: ${colors.shades.info3};
  }
  ${ContainerOutbound} & {
    background-color: ${colors.base.secondary3};
  }
`;

const StyledPopper = styled(Popper)`
  z-index: 1;
`;

const FlagIndicator = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
  color: inherit;
  cursor: default;

  svg {
    width: 12px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const FlagAssigneeName = styled.span`
  font-size: 0.7rem;
  font-weight: bold;
  color: inherit;
  white-space: nowrap;
`;

// Single tappable "failed to send" caption under the bubble — indicator + retry
// action merged into one subtle line (icon + text), the whole line is the retry target.
const RetryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.7rem;
  font-weight: bold;
  color: ${colors.shades.danger3};
  cursor: pointer;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 14px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

/* stylelint-enable no-duplicate-selectors */

export {
  ContainerInbound,
  ContainerOutbound,
  TextMediaContainer,
  SentAt,
  ProfilePictureContainer,
  ThreeDotsButton,
  ThreeDotsContainer,
  ReactionTriggerButton,
  InternalMessage,
  InternalMessageTitle,
  MessageDetailsContainer,
  EditedText,
  ReplyContainer,
  TextWrapper,
  StyledPopper,
  FlagIndicator,
  FlagAssigneeName,
  MessageColumn,
  RetryButton,
  SenderNameInBubble,
};
